'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyBottomBar from '@/components/StickyBottomBar';
import ArticleCard from '@/components/ArticleCard';
import { createClient } from '@/lib/supabase/client';
import { Search, AlertCircle } from 'lucide-react';

const SUGGESTED_CATEGORIES = [
  'Metabolic Research',
  'Regulatory & Global',
  'Compounding',
];

interface DbArticle {
  id: string;
  slug: string;
  title: string;
  category: string;
  category_type: string;
  tag: string;
  excerpt: string;
  body: string;
  author: string;
  published_at: string;
  read_time: number;
  hero_image_url: string;
}

function dbToArticle(a: DbArticle) {
  return {
    id: a.id,
    slug: a.slug,
    title: a.title,
    category: a.category,
    categoryType: (a.category_type as 'hot' | 'analysis'),
    subTag: (a.tag as 'HOT' | 'EXCLUSIVE' | 'ANALYSIS' | 'REPORT' | 'BREAKING' | 'SPONSORED'),
    excerpt: a.excerpt || '',
    body: a.body || '',
    author: a.author,
    authorTitle: '',
    date: new Date(a.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    readTime: a.read_time || 5,
    image: a.hero_image_url || 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=800&q=80',
    imageAlt: `${a.title} — article image`,
    relatedSlugs: [],
  };
}

export default function SearchContent() {
  const params = useSearchParams();
  const query = params?.get('q') || '';

  const [results, setResults] = useState<ReturnType<typeof dbToArticle>[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const runSearch = useCallback(async (q: string) => {
    if (!q.trim()) return;
    setLoading(true);
    setError('');
    setSearched(true);
    try {
      const supabase = createClient();
      // Search title, category, and body using full-text search + ilike fallback
      const { data, error: dbError } = await supabase
        .from('articles')
        .select('id, slug, title, category, category_type, tag, excerpt, body, author, published_at, read_time, hero_image_url')
        .or(`title.ilike.%${q}%,category.ilike.%${q}%,body.ilike.%${q}%`)
        .order('published_at', { ascending: false })
        .limit(30);

      if (dbError) throw dbError;
      setResults((data || []).map(dbToArticle));
    } catch (err: unknown) {
      setError('Search failed. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (query) {
      runSearch(query);
    }
  }, [query, runSearch]);

  return (
    <>
      <Header />
      <main className="pb-20 min-h-screen">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-10">
          {/* Header */}
          <div className="mb-8">
            {query ? (
              <>
                <p className="text-xs font-700 uppercase tracking-widest text-muted-foreground mb-1">Search Results</p>
                <h1 className="text-2xl lg:text-3xl font-800 text-foreground">
                  Results for &ldquo;<span className="text-primary">{query}</span>&rdquo;
                </h1>
                {!loading && searched && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {results.length} article{results.length !== 1 ? 's' : ''} found
                  </p>
                )}
              </>
            ) : (
              <h1 className="text-2xl lg:text-3xl font-800 text-foreground">Search Articles</h1>
            )}
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center gap-3 py-12 text-muted-foreground">
              <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
              <span className="text-sm">Searching articles...</span>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="flex items-center gap-2 py-6 text-red-600 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Results */}
          {!loading && !error && searched && results.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.map((article) => (
                <ArticleCard key={article.id} article={article} size="default" />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && searched && results.length === 0 && (
            <div className="py-16 text-center max-w-md mx-auto">
              <Search size={48} className="text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-700 text-foreground mb-2">No results found</h2>
              <p className="text-sm text-muted-foreground mb-6">
                No articles matched &ldquo;{query}&rdquo;. Try a broader term or browse a category below.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {SUGGESTED_CATEGORIES.map((cat) => (
                  <Link
                    key={cat}
                    href={`/search?q=${encodeURIComponent(cat)}`}
                    className="px-4 py-2 rounded-full border border-border text-sm font-600 text-foreground hover:border-primary hover:text-primary transition-colors"
                  >
                    {cat}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* No query yet */}
          {!query && !loading && (
            <div className="py-16 text-center max-w-md mx-auto">
              <Search size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-6">
                Use the search bar above to find articles on regulatory updates, supply chain news, testing standards, and market data.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {SUGGESTED_CATEGORIES.map((cat) => (
                  <Link
                    key={cat}
                    href={`/search?q=${encodeURIComponent(cat)}`}
                    className="px-4 py-2 rounded-full border border-border text-sm font-600 text-foreground hover:border-primary hover:text-primary transition-colors"
                  >
                    {cat}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <StickyBottomBar />
    </>
  );
}
